import React from 'react';

export interface DayBarData {
  dayIndex: number; // 0..6 (Saturday=0 ... Friday=6)
  date: string;
  seconds: number;
  minutes: number;
  isToday: boolean;
  dayInitial: string;
}

export interface WeekChartProps {
  days: DayBarData[];
  className?: string;
}

export const WeekChart: React.FC<WeekChartProps> = ({ days, className = '' }) => {
  // Find maximum minutes for bar scaling (minimum baseline 60 minutes)
  const maxMinutes = Math.max(60, ...days.map((d) => d.minutes));

  return (
    <section
      className={`bg-base-200 border border-base-300 rounded-2xl p-4 md:p-5 h-full flex flex-col justify-between gap-3 md:gap-4 ${className}`}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-[15px] md:text-base font-semibold text-base-content">این هفته</h2>
        <span className="text-xs text-base-content/60">دقیقه در روز</span>
      </div>

      <div
        role="img"
        aria-label="نمودار دقیقه‌های تمرین در هفت روز گذشته"
        data-testid="week-chart"
        className="h-[110px] md:h-[150px] flex items-end gap-2 md:gap-2.5 pt-4"
      >
        {days.map((day) => {
          const heightRatio = day.minutes > 0 ? Math.min(1, day.minutes / maxMinutes) : 0;
          const heightPercent = day.minutes > 0 ? Math.max(8, Math.round(heightRatio * 100)) : 0;

          return (
            <div
              key={day.dayIndex}
              className="flex-1 h-full flex flex-col items-center justify-end gap-1.5"
            >
              <span
                dir="ltr"
                className={`font-mono text-[11px] font-semibold text-primary-soft shrink-0 select-none ${
                  day.isToday && day.minutes > 0 ? 'visible' : 'invisible'
                }`}
              >
                {day.minutes || 0}
              </span>
              <div
                data-testid={`week-chart-bar-${day.dayIndex}`}
                style={{ height: `${heightPercent}%` }}
                className={`w-full rounded-t md:rounded-t-md rounded-b-[2px] transition-all duration-300 ${
                  day.isToday ? 'bg-primary' : 'bg-base-300'
                }`}
              />
              <span
                className={`text-[11px] shrink-0 ${
                  day.isToday ? 'font-bold text-primary-soft' : 'font-medium text-base-content/80'
                }`}
              >
                {day.isToday ? 'امروز' : day.dayInitial}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

WeekChart.displayName = 'WeekChart';
